import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { LoadingScreen } from '../components/ui/Spinner';
import BalanceCard from '../components/BalanceCard';
import StatisticsSummary from '../components/StatisticsSummary';
import SessionList from '../components/SessionList';
import NewSessionModal from '../components/NewSessionModal';

function DashboardPage() {
    const [loading, setLoading] = useState(true);
    const [bankrollBalance, setBankrollBalance] = useState(0);
    const [stats, setStats] = useState({});
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Memoize the panel classes to prevent recreation on each render
    const panelClasses = useMemo(() => 
        "bg-[#faf8f5] dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm", 
        []
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [bankrollResponse, allTimeStatsResponse, allTimeStatsNoRakebackResponse, mtdStatsResponse, ytdStatsResponse, wtdStatsResponse, mtdStatsNoRakebackResponse, ytdStatsNoRakebackResponse, wtdStatsNoRakebackResponse] = await Promise.all([
                axios.get('/api/bankroll/'),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'ALL_TIME', includeRakeback: true } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'ALL_TIME', includeRakeback: false } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'MTD', includeRakeback: true } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'YTD', includeRakeback: true } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'WTD', includeRakeback: true } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'MTD', includeRakeback: false } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'YTD', includeRakeback: false } 
                }),
                axios.get('/api/statistics-overview/', { 
                    params: { timeframe: 'WTD', includeRakeback: false } 
                })
            ]);

            // Get bankroll balance from the response
            if (bankrollResponse.data?.results?.length > 0) {
                setBankrollBalance(bankrollResponse.data.results[0].current_balance);
            } else if (bankrollResponse.data?.length > 0) {
                setBankrollBalance(bankrollResponse.data[0].current_balance);
            } else {
                // If no bankroll exists, it will be created automatically with calculated balance
                setBankrollBalance(0);
            }

            if (allTimeStatsResponse.data) {
                setStats({ 
                    LTD: {
                        total_profit: allTimeStatsNoRakebackResponse.data.current_total_profit,
                        total_profit_with_rakeback: allTimeStatsResponse.data.current_total_profit,
                        total_deposits: allTimeStatsResponse.data.total_deposits,
                        total_withdrawals: allTimeStatsResponse.data.total_withdrawals,
                        total_sessions: allTimeStatsResponse.data.total_sessions_in_period,
                        total_hours: allTimeStatsResponse.data.total_duration_hours_in_period,
                        total_rakeback: allTimeStatsResponse.data.total_rakeback || 0
                    },
                    YTD: {
                        total_profit: ytdStatsNoRakebackResponse.data?.current_total_profit || 0,
                        total_profit_with_rakeback: ytdStatsResponse.data?.current_total_profit || 0,
                        total_rakeback: ytdStatsResponse.data?.total_rakeback || 0
                    },
                    MTD: {
                        total_profit: mtdStatsNoRakebackResponse.data?.current_total_profit || 0,
                        total_profit_with_rakeback: mtdStatsResponse.data?.current_total_profit || 0,
                        total_rakeback: mtdStatsResponse.data?.total_rakeback || 0
                    },
                    WTD: {
                        total_profit: wtdStatsNoRakebackResponse.data?.current_total_profit || 0,
                        total_profit_with_rakeback: wtdStatsResponse.data?.current_total_profit || 0,
                        total_rakeback: wtdStatsResponse.data?.total_rakeback || 0
                    }
                });
            }
        } catch (error) {
            console.error("Failed to fetch initial data", error);
            setBankrollBalance(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshTrigger]);

    const handleRefresh = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);
    const handleSessionAdded = useCallback(() => {
        handleCloseModal();
        handleRefresh();
    }, [handleCloseModal, handleRefresh]);

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="space-y-4 sm:space-y-6">
                

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                    <div className="lg:col-span-1">
                        <BalanceCard 
                            balance={bankrollBalance} 
                            stats={stats}
                            onUpdate={handleRefresh}
                            className={panelClasses}
                        />
                    </div>
                    <div className="lg:col-span-2">
                        <StatisticsSummary 
                            className={panelClasses} 
                            refreshTrigger={refreshTrigger}
                        />
                    </div>
                </div>

                <SessionList 
                    onUpdate={handleRefresh} 
                    onOpenModal={handleOpenModal} 
                    className={panelClasses}
                />
            </div>

            <NewSessionModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSessionAdded={handleSessionAdded}
            />
        </div>
    );
}

export default React.memo(DashboardPage); 